import { useId, useState, type JSX, type ReactNode } from 'react';

import styles from './ConfirmDialog.module.scss';
import { Modal } from './Modal';

interface ReasonOptions {
  label: string;
  minLength: number;
  maxLength: number;
  placeholder?: string;
  /** OS-1720 — el motivo se puede dejar vacío (queda en la auditoría si se escribe). */
  optional?: boolean;
}

interface ConfirmDialogProps {
  title: string;
  children?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** Acción destructiva: botón de confirmar en rojo. */
  danger?: boolean;
  /** Pide un motivo (obligatorio salvo `optional`) antes de habilitar la confirmación. */
  reason?: ReasonOptions;
  pending?: boolean;
  /** Condición extra del llamador para no permitir confirmar (p. ej. una selección inválida). */
  confirmDisabled?: boolean;
  error?: string | null;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

/**
 * OS-1713 — confirmación reutilizable sobre `Modal`. El foco inicial va a "Cancelar" (o al motivo):
 * un Enter accidental no confirma una acción destructiva. Mientras la acción corre no se cierra.
 */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  cancelLabel = 'Cancelar',
  danger = false,
  reason,
  pending = false,
  confirmDisabled = false,
  error,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): JSX.Element {
  const [text, setText] = useState('');
  const reasonId = useId();
  const hintId = useId();
  const trimmed = text.trim();
  const reasonValid =
    !reason ||
    (reason.optional
      ? trimmed.length <= reason.maxLength
      : trimmed.length >= reason.minLength && trimmed.length <= reason.maxLength);

  return (
    <Modal
      title={title}
      onRequestClose={pending ? undefined : onCancel}
      closeLabel="Cerrar"
      footer={
        <>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={onCancel}
            disabled={pending}
            data-autofocus={reason ? undefined : true}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={danger ? styles.dangerBtn : styles.confirmBtn}
            disabled={pending || !reasonValid || confirmDisabled}
            onClick={() => onConfirm(trimmed)}
          >
            {pending ? 'Procesando…' : confirmLabel}
          </button>
        </>
      }
    >
      <div className={styles.body}>
        {children}
        {reason ? (
          <label className={styles.reason} htmlFor={reasonId}>
            {reason.label}
            {reason.optional ? ' (opcional)' : ' *'}
            <textarea
              id={reasonId}
              data-autofocus
              value={text}
              maxLength={reason.maxLength}
              rows={3}
              placeholder={reason.placeholder}
              aria-describedby={hintId}
              onChange={(e) => setText(e.target.value)}
            />
            <span id={hintId} className={styles.hint}>
              {reason.optional ? '' : `Mínimo ${reason.minLength} caracteres `}(
              {trimmed.length}/{reason.maxLength}).
            </span>
          </label>
        ) : null}
        {error ? (
          <p className={styles.error} role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
